"use client";

import * as React from "react";
import { Link, useLocation } from "@tanstack/react-router";
import {
  LayoutDashboard,
  BarChart3,
  FolderKanban,
  Users,
  Settings,
  CreditCard,
  CalendarDays,
  ChevronRight,
  Command,
  Map,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";

import seedData from "../../../seed-data.json";

const iconMap: Record<string, React.ElementType> = {
  "layout-dashboard": LayoutDashboard,
  "bar-chart-3": BarChart3,
  "folder-kanban": FolderKanban,
  users: Users,
  settings: Settings,
  "credit-card": CreditCard,
  "calendar-days": CalendarDays,
  command: Command,
  map: Map,
};

// Map child ids with hyphens to camelCase translation keys
const childIdToKey: Record<string, string> = {
  "all-projects": "allProjects",
};

interface NavItem {
  id: string;
  label?: string;
  icon: string;
  href: string;
  adminOnly?: boolean;
  children?: { id: string; label?: string; href: string }[];
}

/**
 * AppSidebar - Navigation sidebar
 *
 * Features:
 * - Clean sidebar with semantic token styling
 * - Active states with primary accent
 * - Smooth transitions on all interactions
 * - Admin-only nav items hidden for non-admins
 * - i18n: labels resolved via useTranslation('dashboard')
 */
export function AppSidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const { t } = useTranslation("dashboard");
  const allNavigation = seedData.dashboard.sidebar.navigation as NavItem[];

  // Filter out adminOnly items for non-admin users
  const navigation = allNavigation.filter((item) => !item.adminOnly || isAdmin);

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  const isParentActive = (item: NavItem) => {
    if (isActive(item.href)) return true;
    if (item.children) {
      return item.children.some((child) => isActive(child.href));
    }
    return false;
  };

  const getInitials = (email?: string) => {
    if (!email) return "U";
    return email.split("@")[0]?.slice(0, 2).toUpperCase() || "U";
  };

  // Cast to generic t function for dynamic sidebar key lookups
  const tAny = t as (key: string) => string;
  const getNavLabel = (id: string) => tAny(`sidebar.${id}`);
  const getChildLabel = (id: string) => tAny(`sidebar.${childIdToKey[id] ?? id}`);

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border bg-sidebar">
      <SidebarHeader className="border-b border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/dashboard" className="group">
                <div className="flex aspect-square size-9 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-all">
                  <Command className="size-5" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-sidebar-foreground">
                    {t("sidebar.logo")}
                  </span>
                  <span className="truncate text-xs text-sidebar-foreground/70">
                    {t("sidebar.dashboard")}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="px-2">
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] font-medium tracking-widest text-sidebar-foreground/50 uppercase">
            Navigation
          </SidebarGroupLabel>
          <SidebarMenu>
            {navigation.map((item) => {
              const Icon = iconMap[item.icon] || LayoutDashboard;
              const hasChildren = item.children && item.children.length > 0;
              const active = isParentActive(item);
              const label = getNavLabel(item.id);

              if (hasChildren) {
                return (
                  <Collapsible
                    key={item.id}
                    asChild
                    defaultOpen={active}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          tooltip={label}
                          isActive={active}
                          className={`transition-all duration-200 ${
                            active
                              ? "bg-sidebar-accent text-sidebar-accent-foreground"
                              : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                          }`}
                        >
                          <Icon className="size-4" />
                          <span>{label}</span>
                          <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub className="border-l-sidebar-border">
                          {item.children!.map((child) => {
                            const childActive = isActive(child.href);
                            return (
                              <SidebarMenuSubItem key={child.id}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={childActive}
                                  className={`transition-all duration-200 ${
                                    childActive
                                      ? "text-primary"
                                      : "text-sidebar-foreground/70 hover:text-sidebar-foreground"
                                  }`}
                                >
                                  <Link to={child.href}>
                                    <span>{getChildLabel(child.id)}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              }

              return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    asChild
                    tooltip={label}
                    isActive={active}
                    className={`transition-all duration-200 ${
                      active
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    }`}
                  >
                    <Link to={item.href}>
                      <Icon className="size-4" />
                      <span>{label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="transition-all duration-200 hover:bg-sidebar-accent"
            >
              <Link to="/profile">
                <Avatar className="size-8 border border-sidebar-border">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-primary/10 text-xs font-medium text-primary">
                    {getInitials(user?.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium text-sidebar-foreground">
                    {user?.user_metadata?.full_name || user?.email?.split("@")[0]}
                  </span>
                  <span className="truncate text-xs text-sidebar-foreground/70">{user?.email}</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}

export default AppSidebar;
